import { Users, ShieldCheck, Car, CreditCard, Truck, CalendarDays, UserPlus, UserCheck, Wallet, ClipboardList, TrendingUp } from "lucide-react";
import TrendChart from "./TrendChart";
import PromoAnalyticsPanel from "./PromoAnalyticsPanel";

// Global / cumulative cards (top section)
const GLOBAL_CARDS = [
  { key: "users", label: "Utilisateurs", valueKey: "total_users", subFn: (s) => `${s.total_passengers} passagers`, icon: Users, color: "#0A2540", target: "users" },
  { key: "drivers", label: "Chauffeurs", valueKey: "total_drivers", subFn: (s) => `${s.approved_drivers} approuves, ${s.pending_drivers} en attente`, icon: ShieldCheck, color: "#10B981", target: "drivers" },
  { key: "vehicles", label: "Véhicules", valueKey: "total_vehicles", subFn: () => "Flotte totale", icon: Truck, color: "#3B82F6", target: "vehicles" },
  { key: "rides", label: "Trajets", valueKey: "total_rides", subFn: (s) => `${s.active_rides} actifs`, icon: Car, color: "#FACC15", target: "bookings" },
  { key: "bookings_total", label: "Réservations totales", valueKey: "total_bookings", subFn: (s) => `${s.total_payments} paiements`, icon: ClipboardList, color: "#8B5CF6", target: "bookings" },
  { key: "revenue", label: "Revenus", valueKey: null, subFn: (s) => `${s.total_payments} paiements`, icon: CreditCard, color: "#0A2540", target: "payments" },
];

// Today / "du jour" cards (bottom section)
const TODAY_CARDS = [
  { key: "drivers_today", label: "Nouveaux chauffeurs du jour", valueKey: "new_drivers_today", icon: UserCheck, color: "#10B981", target: "drivers" },
  { key: "clients_today", label: "Nouveaux clients du jour", valueKey: "new_passengers_today", icon: UserPlus, color: "#3B82F6", target: "users" },
  { key: "bookings_today", label: "Réservations du jour", valueKey: "bookings_today", icon: CalendarDays, color: "#FACC15", target: "bookings" },
  { key: "tft_today", label: "TFT rechargé du jour", valueKey: null, valueFn: (s) => `${(s.tft_today_amount || 0).toLocaleString()} TFT`, icon: Wallet, color: "#0A2540", target: "tft-payments" },
];

function renderValue(card, stats) {
  if (typeof card.valueFn === "function") return card.valueFn(stats);
  if (card.valueKey) {
    const v = stats[card.valueKey];
    return typeof v === "number" ? v.toLocaleString() : (v ?? 0);
  }
  return `${(stats.total_revenue || 0).toLocaleString()} XAF`;
}

function Card({ card, stats, onNavigate, testId }) {
  return (
    <button
      onClick={() => onNavigate(card.target)}
      className="bg-white rounded-xl border border-[#CBD5E1] p-5 text-left hover:shadow-md hover:border-[#0A2540]/30 transition-all cursor-pointer"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: card.color + "15" }}>
          <card.icon className="w-4 h-4" style={{ color: card.color }} />
        </div>
      </div>
      <p className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{renderValue(card, stats)}</p>
      <p className="text-xs text-[#64748B]">{card.label}</p>
      {card.subFn && <p className="text-xs text-[#64748B] mt-1">{card.subFn(stats)}</p>}
    </button>
  );
}

export default function StatsPanel({ stats, onNavigate }) {
  if (!stats) return null;
  const todayLabel = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="space-y-6" data-testid="admin-stats-panel">
      {/* Global section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-[#0A2540]" />
          <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Vue d'ensemble</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {GLOBAL_CARDS.map((c) => (
            <Card key={c.key} card={c} stats={stats} onNavigate={onNavigate} testId={`admin-stat-${c.key}`} />
          ))}
        </div>
      </div>

      {/* Today section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="w-4 h-4 text-[#FACC15]" />
          <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Aujourd'hui</h3>
          <span className="text-xs text-[#64748B] capitalize">— {todayLabel}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {TODAY_CARDS.map((c) => (
            <Card key={c.key} card={c} stats={stats} onNavigate={onNavigate} testId={`admin-stat-${c.key}`} />
          ))}
        </div>
      </div>

      {/* Trend chart (last 7 days) */}
      <TrendChart />

      {/* Welcome promo analytics */}
      <PromoAnalyticsPanel />
    </div>
  );
}
